import { defineStore } from 'pinia';
import defaultSearchFilters from '@js/nal/constants/defaultSearchFilters.js';
import nalRequest from '@js/core/nalRequest.js';
import {
    getRegisteredStore,
    getStoreInstancesForBrief,
    getAllRegisteredStores,
} from '@js/piniaStoreRegistry.js';
import {
    normalizeProjectType,
    resolveProjectCandidatesModalDepth,
    getStoreIdFromDepth,
    getDepthFromStoreId,
} from '@js/stores/helpers/projectCandidatesScopeHelpers.js';
import {
    cleanupFilterBoxes as helperCleanupFilterBoxes,
    getFilterBoxTagLabel,
    getFilterLabel,
    compactFilters,
} from '@js/nal/utils/searchFilterHelpers.js';
import {
    mergeBoxesWithPreservedCategory,
    resolveBoxesParameter,
    getFilterTagLabels,
} from '@js/stores/helpers/searchFilterStoreHelpers.js';
import { isAgreementRelevant } from '@js/nal/utils/offLimitAgreementHelpers.js';

import customValues from '@js/vue-mixins/customValues.js';

const STORAGE_KEYS = {
    FILTER_PINNED: 'projectCandidatesFilterPinned',
    FILTERS: 'projectCandidatesFilters',
};

const REGEX_ALL_PROJECT_CANDIDATES = /^projectCandidates/;

// Helper to update pipeline tags array
function updatePipelineTagsArray(pipelineTags, statusOrId, remove = false) {
    if (!Array.isArray(pipelineTags)) {
        return;
    }
    const statusId = typeof statusOrId === 'object' ? statusOrId.id : statusOrId;
    const index = _.findIndex(pipelineTags, { id: statusId });
    if (remove) {
        if (~index) {
            pipelineTags.splice(index, 1);
        }
        return;
    }
    if (~index) {
        pipelineTags.splice(index, 1);
    }
    if (typeof statusOrId === 'object') {
        pipelineTags.push(statusOrId);
    }
}

// ----- Notification helpers for listeners -----
function notifyStores(stores, handlerName, ...args) {
    stores = stores === 'all'
        ? getAllRegisteredStores(REGEX_ALL_PROJECT_CANDIDATES)
        : stores || [];
    stores.forEach(store => {
        if (typeof store[handlerName] === 'function') {
            store[handlerName](...args);
        }
    });
}
// Status updates are project-specific: same candidate has different statuses
// in different projects. Must filter by projectId.
export function notifyStatusAdded(e) {
    const stores = getStoreInstancesForBrief(REGEX_ALL_PROJECT_CANDIDATES, e.projectId);
    notifyStores(stores, 'applyStatusAdded', e);
}
export function notifyStatusRemoved(e) {
    const stores = getStoreInstancesForBrief(REGEX_ALL_PROJECT_CANDIDATES, e.projectId);
    notifyStores(stores, 'applyStatusRemoved', e);
}
export function notifyCandidateRemovedFromProject({ projectId, candidateId }) {
    const stores = getStoreInstancesForBrief(REGEX_ALL_PROJECT_CANDIDATES, projectId);
    notifyStores(stores, 'removeCandidate', candidateId);
}

export function notifyCandidateAdded(briefId, candidate) {
    const stores = getStoreInstancesForBrief(REGEX_ALL_PROJECT_CANDIDATES, briefId);
    notifyStores(stores, 'addCandidate', candidate);
}
// Person-level updates (tags, notes, duplicates, etc.) should broadcast to all
// projects where the person is a candidate. Each store only updates if the
// candidate exists in its list, so cross-project contamination is impossible.
export function notifyManagerTagsUpdated(e) {
    notifyStores('all', 'applyManagerTagsUpdate', e);
}
export function notifyDuplicatesChanged(e) {
    notifyStores('all', 'applyDuplicateChanged', e);
}
export function notifyCustomValuesRemoved(e) {
    notifyStores('all', 'applyCustomValuesRemoval', e);
}
export function notifyCandidateUpdated(candidate) {
    notifyStores('all', 'updateCandidate', candidate);
}
export function notifyCandidateDeleted({ candidateId }) {
    notifyStores('all', 'removeCandidate', candidateId);
}
export function notifyOffLimitsUpdate(agreement, remove = false) {
    notifyStores('all', 'applyOffLimitsUpdate', agreement, remove);
}

const getFiltersStorageKey = (projectType = null) => {
    // Only return a storage key for the three allowed project types (assignment/people/project)
    // Returns null for generic stores without a projectType to prevent unintended storage fallback
    const type = normalizeProjectType(projectType);
    return type ? `${STORAGE_KEYS.FILTERS}_${type}` : null;
};

const getStoredFilters = (projectType) => {
    try {
        const key = getFiltersStorageKey(projectType);
        if (!key) {
            return null;
        }
        const stored = EzekiaLocalStorage.get(key);
        const parsed = stored && JSON.parse(stored);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const getDefaultFilterCategories = () => {
    return [
        { type: 'location', component: 'filter-boxes' },
        { type: 'career', component: 'filter-boxes' },
        { type: 'industry', component: 'filter-boxes' },
        // { type: 'tag_candidate', component: 'filter-boxes' },
        { type: 'tag_person', component: 'filter-boxes' },
        { type: 'tag_task', component: 'filter-boxes' },
        { type: 'classification', component: 'filter-boxes' },
        { type: 'company_id', component: 'filter-company' },
        { type: 'company_type', component: 'filter-boxes' },
        { type: 'project', component: 'filter-boxes' },
    ];
};

const createStoreDefinition = (scopeId = '0') => ({
    state: () => {
        const defaultFilters = defaultSearchFilters();

        // remove unused using getDefaultFilterCategories
        const defaultCategories = getDefaultFilterCategories().map(cat => cat.type);
        Object.keys(defaultFilters).forEach(key => {
            if (!defaultCategories.includes(key)) {
                delete defaultFilters[key];
            }
        });

        const resetFiltersBaseline = _.cloneDeep(defaultFilters);
        const initialFilters = _.cloneDeep(resetFiltersBaseline);

        return {
            // Filter/UI state
            scopeId,
            projectType: null,
            projectId: null,
            _activeBoxesAbort: null,
            _activeBoxesRequestId: 0,
            _lastFetchedBoxesFilters: null,
            searchPanelNarrowWidth: 1000,
            searchPanelMobileWidth: 360,
            searchPanelWidth: null,
            showFilters: EzekiaLocalStorage.get(STORAGE_KEYS.FILTER_PINNED) !== 'false',
            filterPinned: EzekiaLocalStorage.get(STORAGE_KEYS.FILTER_PINNED) !== 'false',
            isLoadingResults: false,
            isLoadingBoxes: false,
            boxes: {},
            defaultFilters,
            resetFiltersBaseline,
            search: {
                filters: initialFilters,
                query: '',
            },
            filterTagLabels: {},
            _applyHandler: null,
            // Candidate/data state (merged from candidateStore)
            candidates: { data: [] },
            loaded: false,
            updating: false,
            initialPageLoaded: false,
            groupingData: {},
        };
    },
    getters: {
        isNarrowView: (state) => {
            return state.searchPanelWidth && state.searchPanelWidth < state.searchPanelNarrowWidth;
        },
        isMobileView: (state) => {
            return state.searchPanelWidth && state.searchPanelWidth < state.searchPanelMobileWidth;
        },
        isFilterPinned() {
            return this.filterPinned && !this.isNarrowView;
        },
        filterCategories() {
            return getDefaultFilterCategories();
        },
        hasId: (state) => {
            return state.projectId !== null && state.projectId !== undefined;
        },
    },
    actions: {
        setProjectId(projectId) {
            if (this.projectId === projectId) {
                return;
            }
            this._cancelBoxesRequest();
            this.projectId = projectId;
            this.boxes = {};
            this._lastFetchedBoxesFilters = null;
            // Reset candidate state when project changes
            this.candidates = { data: [] };
            this.initialPageLoaded = false;
            this.groupingData = {};
        },

        async fetchBoxes({ parameter = '', filters = null } = {}) {
            if (!this.hasId) {
                return;
            }

            const currentFilters = this._withoutCurrentProjectFilter(filters || this.search.filters || {});
            const resolvedParameter = this._resolveBoxesParameter({ parameter, currentFilters });
            const resolvedFilters = this._resolveBoxesFilters({
                parameter: resolvedParameter,
                filters: currentFilters,
            });

            await this._runBoxesRequest({
                parameter: resolvedParameter,
                filters: resolvedFilters,
                currentFilters,
            });
        },
        _resolveBoxesFilters({ parameter, filters }) {
            const sourceFilters = this._withoutCurrentProjectFilter(filters || this.search.filters || {});
            return _.isEmpty(sourceFilters) ? null : sourceFilters;
        },
        _resolveBoxesParameter({ parameter, currentFilters }) {
            const previousFilters = this._lastFetchedBoxesFilters || this.search.filters || {};
            return resolveBoxesParameter({
                parameter,
                currentFilters,
                previousFilters,
            });
        },
        _setLastFetchedBoxesFilters(filters) {
            this._lastFetchedBoxesFilters = _.cloneDeep(filters || {});
        },
        async _runBoxesRequest({ parameter, filters, currentFilters }) {
            this._cancelBoxesRequest();
            const requestId = ++this._activeBoxesRequestId;

            // If a specific category is being refreshed, store its type in isLoadingBoxes
            // so only other categories show overlay. For full refreshes use true.
            this.isLoadingBoxes = parameter || true;

            const query = this.search.query ? `?${this.search.query}` : '';

            try {
                const payload = filters ? { filters } : {};

                const request = nalRequest.raw
                    .post(`/api/projects/${this.projectId}/boxes/${parameter}${query}`, payload);
                this._activeBoxesAbort = typeof request?.abort === 'function' ? () => request.abort() : null;

                const response = await request;
                const mergedBoxes = mergeBoxesWithPreservedCategory({
                    responseData: response.data,
                    parameter,
                    currentBoxes: this.boxes,
                });
                this.boxes = this._withoutCurrentProjectBox(mergedBoxes);
                this._setLastFetchedBoxesFilters(currentFilters);
            } catch (error) {
                if (error?.statusText !== 'abort') {
                    throw error;
                }
            } finally {
                this._finalizeBoxesRequest(requestId);
            }
        },
        _finalizeBoxesRequest(requestId) {
            if (requestId !== this._activeBoxesRequestId) {
                return;
            }

            this._activeBoxesAbort = null;
            this.isLoadingBoxes = false;
        },
        _cancelBoxesRequest() {
            const abort = this._activeBoxesAbort;
            this._activeBoxesAbort = null;

            if (abort) {
                abort();
            }
        },
        _isCurrentProjectId(value) {
            return this.hasId && String(value) === String(this.projectId);
        },
        _withoutCurrentProjectBox(boxes) {
            if (!this.hasId || !Array.isArray(boxes?.project)) {
                return boxes;
            }

            return {
                ...boxes,
                project: boxes.project.filter(box => !this._isCurrentProjectId(box?.value)),
            };
        },
        _withoutCurrentProjectFilter(filters) {
            const source = filters || {};

            if (!this.hasId || !Array.isArray(source.project?.boxes)) {
                return source;
            }

            const nextProjectBoxes = source.project.boxes.filter(boxId => !this._isCurrentProjectId(boxId));

            if (nextProjectBoxes.length === source.project.boxes.length) {
                return source;
            }

            return {
                ...source,
                project: {
                    ...source.project,
                    boxes: nextProjectBoxes,
                },
            };
        },
        getFilterQueryString() {
            const sanitizedFilters = this._withoutCurrentProjectFilter(this.search.filters);
            const cleanedFilters = compactFilters(sanitizedFilters);

            const activeFilters = Object.entries(cleanedFilters)
                .filter(([key, value]) => {
                    if (Array.isArray(value)) {
                        return value.length > 0;
                    }

                    return !!value;
                })
                .reduce((acc, [key, value]) => {
                    acc[key] = value;
                    return acc;
                }, {});

            return Object.keys(activeFilters).length > 0 ? JSON.stringify(activeFilters) : null;
        },
        handleSiteWrapperWidthChange(width) {
            this.searchPanelWidth = width;
            this.showFilters = this.isFilterPinned;
        },
        registerApplyHandler(handler) {
            if (typeof handler !== 'function') {
                return;
            }

            this._applyHandler = handler;
        },
        unregisterApplyHandler(handler) {
            if (typeof handler !== 'function') {
                return;
            }

            if (this._applyHandler === handler) {
                this._applyHandler = null;
            }
        },
        setFilterTagLabel(filterType, label) {
            if (!filterType) {
                return;
            }

            if (label) {
                this.filterTagLabels[filterType] = label;
            } else {
                delete this.filterTagLabels[filterType];
            }
        },
        setSearchQuery(query) {
            this.search.query = query;
        },
        setShowFilters(show) {
            this.showFilters = show;
        },
        getFilterTagLabel(filterType) {
            return this.filterTagLabels[filterType] || null;
        },
        updateFilterTagLabel(category) {
            const filter = this.search.filters[category];
            const boxes = this.boxes[category];

            if (!filter || !Array.isArray(filter.boxes) || !boxes) {
                return;
            }

            this.setFilterTagLabel(category, getFilterBoxTagLabel(filter, boxes));
        },
        updateSearchFilters(newFilters, options = {}) {
            if (!newFilters || typeof newFilters !== 'object') {
                return;
            }

            this.search.filters = _.cloneDeep(newFilters);

            if (!options.skipPersist) {
                this._persistFiltersToStorage();
            }
        },
        async syncFiltersFromStorage({ triggerApply = false } = {}) {
            // Only load filters for the three allowed project types
            const storageKey = getFiltersStorageKey(this.projectType);
            if (!storageKey) {
                return false;
            }

            const storedFilters = getStoredFilters(this.projectType);

            if (!storedFilters || typeof storedFilters !== 'object') {
                return false;
            }

            if (_.isEqual(this.search.filters, storedFilters)) {
                return false;
            }

            this.updateSearchFilters(storedFilters, { skipPersist: true });
            this.filterTagLabels = {};

            if (triggerApply && this._applyHandler) {
                await this._applyHandler(_.cloneDeep(storedFilters));
            }

            return true;
        },
        persistCurrentFiltersToStorage() {
            // Save this store's current filters to localStorage (overwriting previous state)
            // This is called when revisiting a store to make its filters the "last viewed"
            this._persistFiltersToStorage();
        },
        resetFilters() {
            this.filterTagLabels = {};
            this.updateSearchFilters(this.resetFiltersBaseline);
        },
        async applyFilters({ filters = this.search.filters } = {}) {
            if (!filters || typeof filters !== 'object') {
                return;
            }

            const cloned = _.cloneDeep(filters);
            this.updateSearchFilters(cloned);
            this._setActiveFilterTagLabels();

            if (this._applyHandler) {
                this.isLoadingResults = true;
                await this._applyHandler(cloned);
            }
        },
        toggleFilterPinned() {
            this.filterPinned = !this.filterPinned;
            EzekiaLocalStorage.set(STORAGE_KEYS.FILTER_PINNED, this.filterPinned ? 'true' : 'false');
        },
        cleanupFilterBoxes(filtersToClean, availableBoxes) {
            const currentProjectSelection = filtersToClean?.project?.boxes?.find(
                boxId => this._isCurrentProjectId(boxId)
            );

            helperCleanupFilterBoxes(filtersToClean, availableBoxes);

            if (currentProjectSelection === undefined) {
                return;
            }

            const hasCurrentProject = filtersToClean.project.boxes
                .some(boxId => this._isCurrentProjectId(boxId));

            if (!hasCurrentProject) {
                filtersToClean.project.boxes.push(currentProjectSelection);
            }
        },
        _setActiveFilterTagLabels() {
            // Recompute labels from applied filters
            this.filterTagLabels = getFilterTagLabels({
                filters: this.search.filters || {},
                boxes: this.boxes,
                getFilterBoxTagLabel,
                getFilterLabel,
            });
        },
        // ===== Candidate management actions (from candidateStore) =====
        setCandidates(data) {
            this.candidates = data;
        },
        addCandidate(candidate) {
            if (!Array.isArray(this.candidates.data)) {
                this.candidates.data = [];
            }
            this.candidates.data.unshift(candidate);
            if (!this.candidates.meta) {
                this.candidates.meta = { unfilteredTotal: 0 };
            }
            this.candidates.meta.unfilteredTotal += 1;
        },
        addCandidates(newCandidates) {
            if (!Array.isArray(this.candidates.data)) {
                this.candidates.data = [];
            }
            if (!this.candidates.meta) {
                this.candidates.meta = { unfilteredTotal: 0 };
            }
            this.candidates.data.push(...newCandidates);
            this.candidates.meta.unfilteredTotal += newCandidates.length;
        },
        removeCandidate(candidateId) {
            if (!Array.isArray(this.candidates.data)) {
                return;
            }
            const index = this.candidates.data.findIndex(c => c.id === candidateId);
            if (index !== -1) {
                this.candidates.data.splice(index, 1);
                if (this.candidates.meta) {
                    this.candidates.meta.total -= 1;
                    this.candidates.meta.unfilteredTotal -= 1;
                }
            }
        },
        addUnconnectedCandidate(person) {
            if (!this.candidates.meta) {
                this.candidates.meta = {};
            }
            if (!Array.isArray(this.candidates.meta.unconnectedCandidates)) {
                this.candidates.meta.unconnectedCandidates = [];
            }
            if (!this.candidates.meta.unconnectedCandidates.find(candidate =>
                candidate.id === person.id)) {
                this.candidates.meta.unconnectedCandidates.unshift(person);
            }
        },
        replaceCandidates(candidates) {
            if (!Array.isArray(candidates)) {
                this.candidates.data = [];
                return;
            }
            this.candidates.data = candidates;
        },
        clearCandidates() {
            this.candidates = { data: [] };
        },
        updateCandidate(updatedCandidate) {
            if (this.candidates.data) {
                const index = this.candidates.data.findIndex(c => c.id === updatedCandidate.id);
                if (index !== -1) {
                    const oldCandidate = this.candidates.data[index];
                    const mergedCandidate = _.merge({}, oldCandidate, updatedCandidate);
                    this.candidates.data.splice(index, 1, mergedCandidate);
                }
            }
        },
        _applyAgreementChange(localCandidate, agreement, action) {
            if (!localCandidate?.meta) {
                return;
            }

            const agreements = localCandidate.meta.offLimitAgreements || [];
            const index = _.findIndex(agreements, { id: agreement.id });

            if (action === 'remove') {
                if (index === -1) {
                    return;
                }
                agreements.splice(index, 1);
            } else if (action === 'update') {
                if (index !== -1) {
                    agreements.splice(index, 1, agreement);
                } else {
                    agreements.push(agreement);
                }
            }

            localCandidate.meta.offLimitAgreements = agreements;
        },
        _applyCompanyOffLimitsUpdate(agreement, remove = false) {
            if (!Array.isArray(this.candidates.data)) {
                return;
            }

            this.candidates.data.forEach(localCandidate => {
                const hasAgreement = !!localCandidate?.meta?.offLimitAgreements
                    ?.find(item => item.id === agreement.id);

                if (remove) {
                    if (hasAgreement) {
                        this._applyAgreementChange(localCandidate, agreement, 'remove');
                    }
                    return;
                }

                const isRelevant = isAgreementRelevant(localCandidate, agreement, { isCompany: false });

                if (!isRelevant && hasAgreement) {
                    this._applyAgreementChange(localCandidate, agreement, 'remove');
                    return;
                }

                if (isRelevant) {
                    this._applyAgreementChange(localCandidate, agreement, 'update');
                }
            });
        },
        applyOffLimitsUpdate(agreement, remove = false) {
            if (!agreement) {
                return;
            }

            if (agreement.agreementType === 'company') {
                this._applyCompanyOffLimitsUpdate(agreement, remove);
                return;
            }

            const candidateId = agreement.agreementId;
            const localCandidate = this.findCandidate(candidateId);

            if (!localCandidate) {
                return;
            }

            this._applyAgreementChange(localCandidate, agreement, remove ? 'remove' : 'update');

            //this._applyUpdatedAt(localCandidate, agreement);
        },
        // ----- Project-scoped event handlers -----
        _applyStatusChange({ candidate, status }, remove = false) {
            const candidateId = candidate.id;
            const statusId = status.id;
            const statusOrId = remove ? statusId : status;

            if (!candidateId || !statusOrId) {
                return;
            }

            const localCandidate = this.findCandidate(candidateId);

            if (!localCandidate) {
                return;
            }

            const candidatePipelineTags = localCandidate.meta?.candidate?.pipelineTags;

            updatePipelineTagsArray(candidatePipelineTags, statusOrId, remove);
            this._applyUpdatedAt(localCandidate, candidate);
        },
        _applyUpdatedAt(localCandidate, updatedCandidate) {
            const updatedAt = updatedCandidate.updatedAt;
            if (updatedAt) {
                localCandidate.updatedAt = updatedAt;
            }
        },
        applyStatusAdded(e) {
            this._applyStatusChange(e, false);
        },
        applyStatusRemoved(e) {
            this._applyStatusChange(e, true);
        },
        applyManagerTagsUpdate(candidate) {
            const candidateId = candidate.id;
            const managerTags = candidate.manager.tags;

            if (!candidateId || !managerTags) {
                return;
            }

            const localCandidate = this.findCandidate(candidateId);

            if (localCandidate?.manager?.tags) {
                localCandidate.manager.tags = managerTags;
                this._applyUpdatedAt(localCandidate, candidate);
            }
        },
        applyDuplicateChanged(e) {
            const candidateId = Number(e.id);
            if (!candidateId) {
                return;
            }

            const candidate = this.findCandidate(candidateId);
            if (candidate?.manager) {
                candidate.manager.duplicates = e.duplicates;
            }
        },
        applyCustomValuesRemoval(e) {
            if (!this.candidates?.data) {
                return;
            }
            this.candidates.data.forEach(candidate => {
                customValues.methods.removeCustomValuesOnModel(e, candidate);
            });
        },
        setLoading(isLoading) {
            if (isLoading) {
                this.loaded = false;
                this.updating = true;
                this.initialPageLoaded = true;
            } else {
                this.loaded = true;
                this.updating = false;
                this.isLoadingResults = false;
            }
        },
        findCandidate(id) {
            return this.candidates.data?.find(c => c.id === id) || null;
        },
        saveGroupingData(data) {
            this.groupingData = data;
        },
        clearGroupingData() {
            this.groupingData = {};
        },
        _persistFiltersToStorage() {
            const storageKey = getFiltersStorageKey(this.projectType);
            if (!storageKey) {
                return;
            }

            EzekiaLocalStorage.set(storageKey, JSON.stringify(this.search.filters));
        },
    },
});

// Helper to get modal context from window.nal when not explicitly passed
const getModalContext = () => {
    return window.nal?.modal || null;
};

// Helper to find exact registered store or ancestor
const findExistingStore = (storeId, normalizedProjectType = null) => {
    const store = getRegisteredStore(storeId);
    if (store) {
        if (normalizedProjectType && !store.projectType) {
            store.projectType = normalizedProjectType;
        }
        return store;
    }

    return null;
};

// Helper to create and initialize scoped store.
// Accepts an optional `normalizedProjectType` which is set on the store
// at creation time since projectType is immutable for the store instance.
const createAndInitializeStore = (storeId, depth, normalizedProjectType = null) => {
    const useScopedStore = defineStore(storeId, createStoreDefinition(String(depth)));
    const store = useScopedStore();
    if (normalizedProjectType) {
        store.projectType = normalizedProjectType;
    }
    return store;
};

/**
 * Get the project candidates store for the current context.
 */
export const useProjectCandidatesStore = (projectType = null) => {
    const context = getModalContext();
    // Don't use context from a closed modal - only active modals should provide context
    const activeContext = (context?.showModal) ? context : null;

    const normalizedProjectType = normalizeProjectType(projectType);
    const depth = resolveProjectCandidatesModalDepth(activeContext);
    const storeId = getStoreIdFromDepth(depth);

    // Try to using existing store
    const existingStore = findExistingStore(storeId, normalizedProjectType);
    if (existingStore) {
        return existingStore;
    }

    // Create scoped store (pass normalized project type since it won't change)
    if (normalizedProjectType) {
        return createAndInitializeStore(storeId, depth, normalizedProjectType);
    }

    return null;
};

// Helper to collect active scope IDs from modal hierarchy (current modal + backtrace)
const collectActiveSessionIds = () => {
    const sessionIds = new Set();

    const rootSession = window.nal?.modal?.data?.projectCandidatesScopeId;
    if (rootSession) {
        sessionIds.add(rootSession);
    }

    const backtrace = window.nal?.modal?.backtrace;
    if (Array.isArray(backtrace)) {
        backtrace.forEach(entry => {
            const id = entry?.projectCandidatesScopeId;
            if (id) {
                sessionIds.add(id);
            }
        });
    }

    return sessionIds;
};

const shouldPreserveBySessionId = (store) => {
    if (!store?.scopeId) {
        return false;
    }
    const sessionIds = collectActiveSessionIds();
    return sessionIds.has(store.scopeId);
};

// Helper to persist parent store filters when child is disposed.
// With shared storage keys, parent filters should overwrite child filters after modal back/close.
const _persistParentFiltersToStorage = (numericDepth) => {
    if (numericDepth <= 0) {
        return;
    }
    const parentDepth = numericDepth - 1;
    const parentStore = getRegisteredStore(getStoreIdFromDepth(parentDepth));
    parentStore?.persistCurrentFiltersToStorage?.();
};

// eslint-disable-next-line complexity
export const disposeProjectCandidatesScopeStore = ({ depth = null, storeId = null, force = false } = {}) => {
    storeId = storeId || getStoreIdFromDepth(Number(depth));
    const store = getRegisteredStore(storeId);

    if (!store) {
        return;
    }

    depth = depth || getDepthFromStoreId(storeId);

    if (force) {
        // modal closed
        store.$dispose?.();
        _persistParentFiltersToStorage(depth);
        return;
    }

    // Preserve store if still in use
    if (shouldPreserveBySessionId(store)) {
        return;
    }

    store.$dispose?.();
};

export const persistCurrentFiltersToStorage = (depth) => {
    const storeId = getStoreIdFromDepth(Number(depth));
    const store = getRegisteredStore(storeId);
    if (store?.persistCurrentFiltersToStorage) {
        store.persistCurrentFiltersToStorage();
    }
};
